/**
 * TemplatePickerList — the ONE "All templates" list (D6).
 *
 * Search · category filter · grouped rows · tick several · primary "Add N
 * templates" (merges) · quieter "Replace with selected" behind the app's
 * ConfirmationModal (the only destructive path in the feature) · "+ New
 * template" as the last row.
 *
 * PORTALLED to document.body and positioned off the opener's rect: the pad sets
 * `overflow: hidden` and the Visit Actions accordion is narrow, so an in-tree
 * popover would be clipped there (mockup 04/05 tab M).
 *
 * Every count, ordering and grouping comes from `selectByCategory` — this
 * component never sorts or counts anything itself.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_KEYS,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CATEGORY_EMPTY_COPY,
  TEMPLATE_DELETE_COPY,
  TEMPLATE_ERROR_COPY,
  TEMPLATE_ROW_COPY,
  WHOLE_PRESCRIPTION,
  TEMPLATE_SORT_KEYS,
  TEMPLATE_SORT_COPY,
  DEFAULT_TEMPLATE_SORT,
  buildListGroups,
  listCountLine,
  templatePreviewLine,
  categoryKeyOf,
} from '../../utils/rxTemplates';

export const ALL_CATEGORIES = 'ALL';

/** Below this MEASURED PANEL width the row drops its secondary columns (D30). */
export const NARROW_PANEL_PX = 460;

/** At or above this viewport width the list opens in its wide window (D30). */
export const WIDE_HOST_PX = 1024;

/**
 * The window itself — one place, so the panel and its test read the same
 * numbers. Wide hosts get a bigger window because the doctor is scanning 25
 * rows at 52px each; both are clamped to the viewport, and `place()` still
 * shifts/flips the panel so it can never hang off an edge.
 */
export const PANEL_WINDOW = {
  wide: { width: 'min(46rem, calc(100vw - 2rem))' },
  normal: { width: 'min(34rem, calc(100vw - 1rem))' },
  maxWidth: 'calc(100vw - 1rem)',
  // 50rem, not 44 — at 52px rows the ratified 44rem cap showed ~8 rows on a
  // 1080p pad and the owner asked for ten. Still viewport-clamped by the 72vh
  // leg, so a short screen is unaffected.
  maxHeight: 'min(72vh, 50rem)',
};

const TEAL = '#0d9488';

/**
 * The five category hues — day and night. They live INSIDE this list on
 * purpose: D18 took them off the pad ("position, not colour, says which section
 * a row serves") and explicitly kept them here, where a row can belong to any
 * category and colour is the fastest way to tell them apart.
 */
const CATEGORY_HUES = {
  [WHOLE_PRESCRIPTION]: {
    light: { tint: '#f5f3ff', border: '#c4b5fd', text: '#6d28d9' },
    dark: { tint: 'rgba(139,92,246,0.20)', border: '#8b5cf6', text: '#c4b5fd' },
  },
  [TEMPLATE_CATEGORIES.SYMPTOMS]: {
    light: { tint: '#f0fdfa', border: '#5eead4', text: '#0f766e' },
    dark: { tint: 'rgba(13,148,136,0.20)', border: '#14b8a6', text: '#5eead4' },
  },
  [TEMPLATE_CATEGORIES.DIAGNOSIS]: {
    light: { tint: '#eef2ff', border: '#a5b4fc', text: '#4338ca' },
    dark: { tint: 'rgba(99,102,241,0.22)', border: '#818cf8', text: '#a5b4fc' },
  },
  [TEMPLATE_CATEGORIES.MEDICINES]: {
    light: { tint: '#eff6ff', border: '#93c5fd', text: '#2563eb' },
    dark: { tint: 'rgba(59,130,246,0.22)', border: '#3b82f6', text: '#93c5fd' },
  },
  [TEMPLATE_CATEGORIES.LAB_TESTS]: {
    light: { tint: '#fffbeb', border: '#f0b429', text: '#b45309' },
    dark: { tint: 'rgba(245,158,11,0.20)', border: '#f59e0b', text: '#fbbf24' },
  },
  // Same purple identity the Advised surgeries pad section and its read-only
  // chip already use (FE-6/FE-7) — a template row for this category should
  // read as the SAME thing as the section it came from, not a fresh hue.
  [TEMPLATE_CATEGORIES.SURGERIES]: {
    light: { tint: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
    dark: { tint: 'rgba(124,58,237,0.20)', border: '#a78bfa', text: '#c4b5fd' },
  },
};

/**
 * The row's CSS block. Everything here NEEDS a pseudo-class, which an inline
 * style cannot express:
 *  - the TICK. A native `<input type="checkbox">` ignores width/height in
 *    Chrome and Safari and paints at its intrinsic ~13px — which is exactly why
 *    D29 asked for a bigger box and the doctor still could not hit it.
 *    `appearance:none` hands the size back; the check mark then has to be drawn
 *    (inline SVG data-URI, no new asset).
 *  - `:hover` on the row and the action buttons.
 * The row's own state colours stay INLINE, where React already knows them.
 */
const rowStyleSheet = (isDark) => `
  .rx-tpl-row {
    display: flex; align-items: flex-start; gap: 0.625rem;
    padding: 0.625rem 0.75rem; min-height: 3.25rem; cursor: pointer;
    border-bottom: 1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15,23,42,0.10)'};
    /* reserved, so a ticked row's bar cannot shift the text sideways */
    border-left: 3px solid transparent;
  }
  .rx-tpl-row[data-ticked="false"]:hover { background: ${isDark ? '#1e293b' : '#f1f5f9'}; }
  .rx-tpl-cb {
    -webkit-appearance: none; appearance: none; margin: 0;
    width: 1.375rem; height: 1.375rem; flex: 0 0 1.375rem;
    margin-top: 0.0625rem;
    border: 2px solid ${isDark ? '#64748b' : '#94a3b8'}; border-radius: 0.375rem;
    background: ${isDark ? '#1e293b' : '#ffffff'};
    cursor: pointer; display: inline-block; position: relative;
  }
  .rx-tpl-cb:hover { border-color: ${TEAL}; }
  .rx-tpl-cb:checked {
    background-color: ${TEAL}; border-color: ${TEAL};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6 9 17l-5-5'/%3E%3C/svg%3E");
    background-size: 0.875rem 0.875rem; background-position: center; background-repeat: no-repeat;
  }
  .rx-tpl-cb:focus-visible {
    outline: none; border-color: ${TEAL};
    box-shadow: 0 0 0 3px ${isDark ? 'rgba(45,212,191,0.45)' : 'rgba(13,148,136,0.35)'};
  }
  .rx-tpl-btn:hover { border-color: ${TEAL}; }
`;

export default function TemplatePickerList({
  templates,
  loading = false,
  error = null,
  onRetry,
  initialCategory = ALL_CATEGORIES,
  // bk-988 — the text the field was filtered by when the doctor asked to see
  // them all. The list opens already narrowed to it, and he can clear it here.
  initialQuery = '',
  anchorRect = null,
  anchorEl = null,      // the opener node: outside-click ignores it (the opener
                        // toggles) and Esc returns focus to it
  onAdd,
  onReplace,
  onNewTemplate,
  onEdit,               // present ⇒ each row offers Edit (D15)
  onDelete,             // present ⇒ each row offers Delete (D16); the caller
                        // does the API call and drops the row from its own list
  onClose,
  // D26 — the doctor's sort, OWNED BY THE PAD (it also orders the strip's chips
  // and the on-focus rows, so one owner is the only way the three agree). The
  // control is not rendered where no owner is passing it down.
  sort = DEFAULT_TEMPLATE_SORT,
  onSortChange = null,
  isDark = false,
}) {
  const [query, setQuery] = useState(initialQuery || '');
  const [category, setCategory] = useState(initialCategory || ALL_CATEGORIES);
  const [ticked, setTicked] = useState(() => new Set());
  const [highlight, setHighlight] = useState(-1);
  const [confirmReplace, setConfirmReplace] = useState(false);
  // The row waiting on the delete confirmation (D16). Null = nothing asked.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const searchRef = useRef(null);
  const rowRefs = useRef({});
  const panelRef = useRef(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  // Esc closes and hands focus back to the opener. Listened for on the
  // document, not just the panel, so it still works when focus has wandered
  // out of the portalled popover.
  const closeToOpener = useCallback(() => {
    onClose();
    anchorEl?.focus?.();
  }, [onClose, anchorEl]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); closeToOpener(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeToOpener]);

  // Click/tap outside closes. The panel is PORTALLED, so this listens on the
  // document and skips (a) the panel itself, (b) the opener — whose own click
  // toggles the list, and which would otherwise close-then-reopen — and (c) the
  // Replace confirmation modal, which renders outside the panel.
  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorEl?.contains?.(e.target)) return;
      if (e.target.closest?.('.confirmation-overlay')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [onClose, anchorEl]);

  // Rows actually on screen — ONE shared derivation (the Rx Templates card
  // browses through the very same function), feeding the count line, the
  // keyboard walk, the "Add N" label and the empty states.
  const { selection, groups, shown, matched } = useMemo(
    () => buildListGroups(templates || [], {
      query, category: category === ALL_CATEGORIES ? null : category, sort,
    }),
    [templates, category, query, sort]
  );

  const flatRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  // The count line names the filter that is actually applied (D18).
  const countLine = listCountLine(shown, matched, category === ALL_CATEGORIES ? null : category);

  useEffect(() => {
    if (highlight < 0) return;
    rowRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const toggle = useCallback((id) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const tickedTemplates = useMemo(
    () => (templates || []).filter((t) => ticked.has(t.template_id)),
    [templates, ticked]
  );
  const tickedCount = tickedTemplates.length;

  // A whole-prescription template does not merge into one section — it stands
  // in for everything already written, and the confirm says so in the ratified
  // words. Replace path only.
  const replaceConfirmMessage = tickedTemplates.some((t) => categoryKeyOf(t) === WHOLE_PRESCRIPTION)
    ? `What you have written will be replaced by the selected template. This cannot be undone. ${TEMPLATE_ROW_COPY.wholePrescriptionReplaceLine}`
    : 'What you have written will be replaced by the selected template. This cannot be undone.';

  const runAdd = useCallback(() => {
    if (tickedCount === 0) return;
    onAdd(tickedTemplates);
    onClose();
  }, [tickedCount, tickedTemplates, onAdd, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, flatRows.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1)); return; }
    if (e.key === ' ' && highlight >= 0 && e.target.tagName !== 'INPUT') {
      e.preventDefault(); toggle(flatRows[highlight].template_id); return;
    }
    // S2 AC7 — Enter runs the primary action (Add); Space is what ticks a row.
    if (e.key === 'Enter') { e.preventDefault(); runAdd(); }
  };

  // ── placement ───────────────────────────────────────────────────────
  // The popover is portalled and `position: fixed`, so nothing above it can
  // constrain it — which also means nothing above it stops it hanging off the
  // right or bottom edge. Measure the panel and SHIFT it left / FLIP it above
  // the opener until it fits, re-measured on resize (and on scroll, which
  // moves the anchor under it).
  const VIEWPORT_MARGIN = 8;
  const [pos, setPos] = useState({ top: -9999, left: -9999, above: false });
  // D30 — the row's narrow variant reads the PANEL's own measured width, not
  // the viewport: the same list is 34rem on the wide pad and ~380px inside the
  // Visit Actions accordion on the very same screen. It rides the placement
  // measure below — no second listener, no observer.
  const [narrow, setNarrow] = useState(false);
  // D30 (owner, live run: "the list window is too small") — a WIDE host gets a
  // wider window. Measured off the viewport the portal actually lives in, in
  // the same pass as the placement below.
  const [wideHost, setWideHost] = useState(false);
  useLayoutEffect(() => {
    const place = () => {
      const el = panelRef.current;
      if (!el) return;
      const { width, height } = el.getBoundingClientRect();
      // width 0 (jsdom, first paint) is never "narrow"
      setNarrow((prev) => {
        const next = width > 0 && width < NARROW_PANEL_PX;
        return prev === next ? prev : next;
      });
      setWideHost((prev) => {
        const next = (window.innerWidth || 0) >= WIDE_HOST_PX;
        return prev === next ? prev : next;
      });
      const vw = window.innerWidth || 1024;
      const vh = window.innerHeight || 768;
      const a = anchorRect || { top: 80, bottom: 80, left: VIEWPORT_MARGIN };

      let left = Math.min(a.left, vw - width - VIEWPORT_MARGIN);
      left = Math.max(VIEWPORT_MARGIN, left);

      let top = a.bottom + 6;
      let flipped = false;
      if (top + height > vh - VIEWPORT_MARGIN) {
        const above = a.top - height - 6;                 // flip above the opener
        flipped = above >= VIEWPORT_MARGIN;
        top = flipped ? above : Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN);
      }
      setPos((prev) => (
        prev.top === top && prev.left === left && prev.above === flipped
          ? prev
          : { top, left, above: flipped }
      ));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
    // Re-place when the anchor or the content height can have changed.
  }, [anchorRect, groups, shown, loading, error]);

  // ── styles (solid day + dark surfaces, visible borders in both) ──────
  const surface = isDark ? '#0f172a' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.18)';
  const ink = isDark ? '#e2e8f0' : '#1e293b';
  const inkFaint = isDark ? '#94a3b8' : '#64748b';
  const controlBg = isDark ? '#1e293b' : '#ffffff';
  // The summary line is READABLE muted, not the faint grey the category label
  // used to share with it (three greys, nothing for the eye to land on).
  const inkSummary = isDark ? '#cbd5e1' : '#475569';
  const ROW_TICKED_BG = isDark ? 'rgba(13,148,136,0.30)' : '#ccfbf1';
  const rowActionStyle = {
    flexShrink: 0, minHeight: '2rem', padding: narrow ? '0.3rem' : '0.3rem 0.65rem',
    width: narrow ? '2rem' : undefined,
    borderRadius: '0.375rem', cursor: 'pointer',
    fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap',
  };



  // D22 interactive floor (0.8125rem) — this panel is PORTALLED, so the pad's
  // own type-scale census never saw it.
  const pill = (active) => ({
    padding: '0.25rem 0.6rem', borderRadius: '2rem', cursor: 'pointer',
    fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap',
    border: `1px solid ${active ? '#0d9488' : border}`,
    background: active ? '#0d9488' : controlBg,
    color: active ? '#ffffff' : ink,
  });

  const body = () => {
    if (loading) return <div style={{ padding: '0.75rem', fontSize: '0.8125rem', color: inkFaint }}>Loading your templates…</div>;
    if (error) {
      return (
        <div style={{ padding: '0.75rem', fontSize: '0.8125rem', color: ink, display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
          <span>{TEMPLATE_ERROR_COPY.line}</span>
          <button type="button" onClick={onRetry} style={{ ...pill(false), padding: '0.3rem 0.75rem' }}>{TEMPLATE_ERROR_COPY.retry}</button>
        </div>
      );
    }
    if (selection.total === 0) {
      return (
        <div style={{ padding: '0.75rem', fontSize: '0.8125rem', color: ink }}>
          <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>No templates yet</div>
          <div style={{ color: inkFaint }}>
            Save what you write often — symptoms, a diagnosis, medicines or lab tests — and reuse it in one click.
          </div>
        </div>
      );
    }
    if (shown === 0) {
      return (
        <div style={{ padding: '0.75rem', fontSize: '0.8125rem', color: inkFaint }}>
          {query
            ? `Nothing matches "${query}" in your templates.`
            : TEMPLATE_CATEGORY_EMPTY_COPY[category]}
        </div>
      );
    }
    let idx = -1;
    return groups.map((g) => (
      <div key={g.key}>
        {category === ALL_CATEGORIES && (
          <div style={{
            padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em', color: inkFaint,
            background: isDark ? '#1e293b' : '#f8fafc',
          }}>{TEMPLATE_CATEGORY_LABELS[g.key]}</div>
        )}
        {g.rows.map((t) => {
          idx += 1;
          const i = idx;
          const isTicked = ticked.has(t.template_id);
          const isKb = highlight === i;
          const hue = CATEGORY_HUES[categoryKeyOf(t)][isDark ? 'dark' : 'light'];
          return (
            // D29 behaviour, D30 anatomy. The WHOLE ROW is the label — clicking
            // the name, the pill or the summary ticks it — and the tick is a
            // 22px CUSTOM box: a native checkbox ignores width/height in Chrome
            // and Safari, which is why D29's `1.25rem` never landed on screen.
            // Edit/Delete keep their own actions (they stop the label's
            // activation below).
            <label
              key={t.template_id}
              ref={(el) => { rowRefs.current[i] = el; }}
              className="rx-tpl-row"
              data-ticked={isTicked ? 'true' : 'false'}
              data-kb={isKb ? 'true' : 'false'}
              style={{
                // Three SEPARATE state channels — a row can be ticked AND carry
                // the keyboard position, so neither may borrow the other's
                // signal, and neither is an opacity change.
                background: isTicked ? ROW_TICKED_BG : 'transparent',
                borderLeftColor: isTicked ? TEAL : 'transparent',
                outline: isKb ? `2px solid ${TEAL}` : 'none',
                outlineOffset: '-2px',
              }}
            >
              <input
                type="checkbox"
                className="rx-tpl-cb"
                checked={isTicked}
                onChange={() => toggle(t.template_id)}
                // The row's text is the label; this keeps the control's
                // accessible name the template's NAME, not the whole row.
                aria-label={t.name}
              />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
                  <span style={{
                    fontSize: '1rem', fontWeight: 600, lineHeight: 1.35, color: ink,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{t.name}</span>
                  {/* The five category hues live INSIDE this list — D18 removed
                      them from the pad and explicitly kept them here. */}
                  <span style={{
                    // The mockup draws this at 0.6875rem; D22's chrome floor is
                    // 0.75rem and the census pins it, so the pill sits ON the
                    // floor. Still two steps below the 1rem name.
                    flexShrink: 0, fontSize: '0.75rem', fontWeight: 700,
                    padding: '0.1rem 0.5rem', borderRadius: '2rem', whiteSpace: 'nowrap',
                    color: hue.text, background: hue.tint, border: `1px solid ${hue.border}`,
                  }}>{TEMPLATE_CATEGORY_LABELS[categoryKeyOf(t)]}</span>
                </span>
                {!narrow && (
                  <span style={{
                    display: 'block', fontSize: '0.875rem', lineHeight: 1.4, color: inkSummary,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    marginTop: '0.125rem',
                  }}>{templatePreviewLine(t)}</span>
                )}
              </span>
              {/* `used n×` is its own end column now — same words, new position */}
              {!narrow && t.usage_count ? (
                <span style={{
                  flexShrink: 0, fontSize: '0.8125rem', color: inkFaint, whiteSpace: 'nowrap',
                  paddingTop: '0.15rem', fontVariantNumeric: 'tabular-nums',
                }}>{`used ${t.usage_count}×`}</span>
              ) : null}
              {onEdit && (
                <button
                  type="button"
                  className="rx-tpl-btn"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(t); }}
                  aria-label={`Edit template "${t.name}"`}
                  style={{
                    ...rowActionStyle,
                    border: `1px solid ${border}`, background: controlBg, color: ink,
                  }}
                >{narrow ? '✎' : 'Edit'}</button>
              )}
              {/* Delete (D16) — every row here belongs to the signed-in doctor
                  (the list endpoint is keyed by member), so no ownership test
                  is possible or needed client-side. Always behind the shared
                  ConfirmationModal below; this button deletes nothing. */}
              {onDelete && (
                <button
                  type="button"
                  className="rx-tpl-btn"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(t); }}
                  aria-label={TEMPLATE_DELETE_COPY.rowAriaLabel(t.name)}
                  style={{
                    ...rowActionStyle,
                    border: `1px solid ${isDark ? 'rgba(248,113,113,0.5)' : 'rgba(220,38,38,0.45)'}`,
                    background: isDark ? '#1e293b' : '#fef2f2',
                    color: isDark ? '#fca5a5' : '#b91c1c',
                  }}
                >{narrow ? '🗑' : TEMPLATE_DELETE_COPY.rowActionLabel}</button>
              )}
            </label>
          );
        })}
      </div>
    ));
  };

  const addLabel = tickedCount === 0
    ? 'Add templates'
    : `Add ${tickedCount} template${tickedCount === 1 ? '' : 's'}`;

  return createPortal(
    <>
      <div
        ref={panelRef}
        data-testid="template-picker-list"
        role="dialog"
        aria-label="All templates"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed', top: pos.top, left: pos.left,
          // Wide enough that the category pills fit on ONE line in the common
          // case, never wider than the viewport. On a wide host the doctor is
          // scanning 25 rows at 52px each, so the window opens wider and
          // taller — still bounded to the viewport, and still flipped/shifted
          // by `place()` so it can never hang off an edge.
          width: (wideHost ? PANEL_WINDOW.wide : PANEL_WINDOW.normal).width,
          maxWidth: PANEL_WINDOW.maxWidth,
          zIndex: 1000, background: surface, color: ink,
          border: `1px solid ${border}`, borderRadius: '0.5rem',
          // The panel hangs off its opener — a 2px teal rule on the edge facing
          // it says WHICH control opened this (owner, live run).
          [pos.above ? 'borderBottom' : 'borderTop']: `2px solid ${TEAL}`,
          boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
          fontFamily: 'system-ui, sans-serif',
          display: 'flex', flexDirection: 'column', maxHeight: PANEL_WINDOW.maxHeight,
        }}
        data-window={wideHost ? 'wide' : 'normal'}
      >
        <style>{rowStyleSheet(isDark)}</style>
        <div style={{ padding: '0.5rem', borderBottom: `1px solid ${border}`, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: 700, color: ink }}>All templates</span>
            <button
              type="button"
              onClick={closeToOpener}
              aria-label="Close"
              style={{
                flexShrink: 0, width: '1.5rem', height: '1.5rem', lineHeight: 1,
                borderRadius: '0.375rem', cursor: 'pointer',
                border: `1px solid ${border}`, background: controlBg, color: ink,
                fontSize: '0.8125rem', fontWeight: 700,
              }}
            >✕</button>
          </div>
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlight(-1); }}
            placeholder="Search your templates…"
            aria-label="Search your templates"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem',
              borderRadius: '0.375rem', border: `1px solid ${border}`,
              background: controlBg, color: ink, fontSize: '0.875rem', outline: 'none',
            }}
          />
          {/* D26 — the sort, next to the search box. Same pill legs as the
              category filter (solid in BOTH states, never bg-transparent), a
              real radiogroup so the keyboard reaches every option. */}
          {onSortChange && (
            <div
              data-testid="template-sort"
              role="radiogroup"
              aria-label={TEMPLATE_SORT_COPY.label}
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem' }}
            >
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: inkFaint }}>
                {TEMPLATE_SORT_COPY.label}
              </span>
              {TEMPLATE_SORT_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={sort === key}
                  onClick={() => { onSortChange(key); setHighlight(-1); }}
                  style={pill(sort === key)}
                >
                  {TEMPLATE_SORT_COPY.option[key]}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            <button type="button" style={pill(category === ALL_CATEGORIES)}
              onClick={() => { setCategory(ALL_CATEGORIES); setHighlight(-1); }}>
              {`All · ${selection.total}`}
            </button>
            {TEMPLATE_CATEGORY_KEYS.filter((k) => selection.counts[k] > 0 || k === category).map((k) => (
              <button key={k} type="button" style={pill(category === k)}
                onClick={() => { setCategory(k); setHighlight(-1); }}>
                {`${TEMPLATE_CATEGORY_LABELS[k]} · ${selection.counts[k]}`}
              </button>
            ))}
          </div>
          {countLine && (
            <div style={{ fontSize: '0.75rem', color: inkFaint }}>{countLine}</div>
          )}
        </div>

        {/* ONLY the rows scroll — the header above and the footer below stay
            in reach however long the list is (D30). */}
        <div data-testid="template-picker-scroll" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {body()}
        </div>

        <button
          type="button"
          onClick={onNewTemplate}
          style={{
            display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', flexShrink: 0,
            padding: '0.5rem 0.6rem', border: 'none', borderTop: `1px solid ${border}`,
            background: controlBg, color: isDark ? '#5eead4' : '#0f766e',
            fontSize: '0.875rem', fontWeight: 700, fontFamily: 'system-ui, sans-serif',
          }}
        >+ New template</button>

        {selection.total > 0 && !loading && !error && (
          <div style={{ padding: '0.5rem', borderTop: `1px solid ${border}`, display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setConfirmReplace(true)}
              disabled={tickedCount === 0}
              style={{
                padding: '0.4rem 0.75rem', borderRadius: '0.375rem', cursor: tickedCount ? 'pointer' : 'default',
                border: `1px solid ${border}`, background: controlBg, color: ink,
                fontSize: '0.8125rem', fontWeight: 600, opacity: tickedCount === 0 ? 0.6 : 1,
              }}
            >Replace with selected</button>
            <button
              type="button"
              onClick={runAdd}
              disabled={tickedCount === 0}
              title={tickedCount === 0 ? 'Select a template to add.' : undefined}
              style={{
                flex: 1, padding: '0.4rem 0.75rem', borderRadius: '0.375rem',
                cursor: tickedCount ? 'pointer' : 'default', border: 'none',
                background: '#0d9488', color: '#ffffff', fontSize: '0.8125rem', fontWeight: 700,
                opacity: tickedCount === 0 ? 0.6 : 1,
              }}
            >{addLabel}</button>
          </div>
        )}
        {tickedCount === 0 && selection.total > 0 && !loading && !error && (
          <div style={{ padding: '0 0.5rem 0.5rem', fontSize: '0.75rem', color: inkFaint }}>
            Select a template to add.
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmReplace}
        title="Replace this prescription?"
        message={replaceConfirmMessage}
        ctaLabel="Replace"
        variant="danger"
        icon="⚠️"
        cancelLabel="Keep what I have written"
        onConfirm={() => { setConfirmReplace(false); onReplace(tickedTemplates); onClose(); }}
        onCancel={() => setConfirmReplace(false)}
      />

      {/* Delete confirmation (D16) — the SAME strings the Rx Templates card
          shows, never a native dialog, initial focus on "Keep it". The list
          stays open: the deleted row simply leaves the `templates` prop. */}
      <ConfirmationModal
        isOpen={!!confirmDelete}
        title={TEMPLATE_DELETE_COPY.confirmTitle}
        message={confirmDelete ? TEMPLATE_DELETE_COPY.confirmBody(confirmDelete.name) : ''}
        ctaLabel={TEMPLATE_DELETE_COPY.confirmDangerButton}
        cancelLabel={TEMPLATE_DELETE_COPY.confirmKeepButton}
        variant="danger"
        icon="🗑"
        autoFocusCancel
        onConfirm={() => { const t = confirmDelete; setConfirmDelete(null); onDelete(t); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </>,
    document.body
  );
}
